import { Injectable } from '@angular/core';
import olMap from 'ol/Map';
import View from 'ol/View';
import OSM from 'ol/source/OSM';
import * as olProj from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import {Draw, Select} from 'ol/interaction';
import TileWMS from 'ol/source/TileWMS';

import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import * as proj4x from 'proj4';

import WMTS, {optionsFromCapabilities} from 'ol/source/WMTS';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import {getWidth} from 'ol/extent';


import {register} from 'ol/proj/proj4';
import { ConfigurationLayerModel } from '../models/configuration';
import { ConfigurationService } from './configuration.service';
import { ParentLayers } from '../menu/view-menu/parent-layer';
import {ScaleLine, defaults as defaultControls, MousePosition} from 'ol/control';
import * as olCoordinate from 'ol/coordinate';
import {
  defaults as defaultInteractions,
} from 'ol/interaction';
import { CoordinatesService } from './coordinates.service';
import { CoordenateService } from './coordenate.service';
import { TopLevelLayers } from '../menu/view-menu/top-level-layers';
import { Layer, VectorLayerDMTR } from '../menu/view-menu/layer';

import * as olExtent from 'ol/extent';
import { Feature } from 'ol';
import Geometry from 'ol/geom/Geometry';
import { ModalControlService } from './modal-control.service';
import BaseLayer from 'ol/layer/Base';
import BaseTileLayer from 'ol/layer/BaseTile';
import { HttpClient } from '@angular/common/http';
import GeoJSON from 'ol/format/GeoJSON';
import { UserSettingsService } from './userSettings.service';
import { Fill, Stroke, Style,Text } from 'ol/style';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import { cloneDeep, isEqual } from 'lodash';
import { UtilsService } from './utils.service';
import { UserLayer } from '../models/UserSettings';
import MultiLineString from 'ol/geom/MultiLineString';
import { UserAddedLayersService } from './userAddedLayers-management.service';
import TileSource from 'ol/source/Tile';
import { UiBlockService } from './ui-block.service';
import { MultiPoint } from 'ol/geom';
import { EventListenersOnMap } from '../utils/eventListenersOnMap';
import { firstValueFrom } from 'rxjs';
import { LatLonDMS } from '../models/coord-dms';


@Injectable({
  providedIn: 'root'
})
export class MapService {

  static readonly SHOW_TEXT_ON_MAP_ZOOM_LIMIT: number = 5.66;

  private map: olMap;
  private mousePointer : MousePosition;
  selectSingleClick = new Select();
  public coordinates: string = "00.0000, 00.0000";
  public latitude : string = "N00º 00' 00";
  public longitude : string = "E00º 00' 00";
  selectionSource:VectorSource;
  selectionLayer:VectorLayer<VectorSource>;
  layers:ParentLayers[];
  //paramaters for selection buttons
  draw:Draw;
  select:Select;
  eurocontrolLayer:TileLayer<TileSource>;
  identifyToolEnabled:boolean = false;

  // zIndex => it is the index of each layer with respect to its order on the map.
  // it has to do with moving features to front and back
  layerFrontZIndex = 100; // gets incremented each time a layers is moved to front
  layerBackZIndex = -100; // gets decremented each time a layers is moved to back

  constructor(public configurationService : ConfigurationService,
    public rcsControl: CoordenateService,
    private modalService: ModalControlService,
    public http: HttpClient,
    public userSettingsService: UserSettingsService,
    private utilsService : UtilsService,
    public userAddedLayersService: UserAddedLayersService,
    public uiBlock: UiBlockService
    ) {
  }

  async initMapUI() {
    let currentConfiguration = this.configurationService.currentConfiguration;
    if (!currentConfiguration) {
      currentConfiguration = await firstValueFrom(this.configurationService.getActiveConfiguration());
    }

    this.getMap('map').on('pointermove', (evt) => {

      if (this.configurationService.currentConfiguration) {
        currentConfiguration = this.configurationService.currentConfiguration;
      }

      //TODO: Lat long according to projection. Error
      var coords = olProj.toLonLat(evt.coordinate, this.rcsControl.activeRCS.isactive ? this.rcsControl.activeRCS.epsg : "EPSG:3857");
      var lat = coords[1].toFixed(4);
      var lon = coords[0].toFixed(4);
      var lat_formated = '';
      var lon_formated = '';

      lat_formated = this.addLatLeadingZeros(lat);
      lon_formated = this.addLonLeadingZeros(lon);

      this.coordinates = lat_formated + ", " + lon_formated;
      if (currentConfiguration.display.coordinateFormat == "Decimal") {
        this.latitude = lat_formated;
        this.longitude = lon_formated;
      } else if (currentConfiguration.display.coordinateFormat == "DMS") {
        let latLonDms: LatLonDMS = new LatLonDMS();
        latLonDms.fromLat(coords[1]);
        latLonDms.fromLon(coords[0]);
        let aux_lat_degres = this.addLatLeadingZeros(latLonDms.latitude.degrees.toString());
        let aux_lat_minutes = latLonDms.latitude.minutes < 10 ? ("0" + latLonDms.latitude.minutes).slice(-2) : latLonDms.latitude.minutes;
        let aux_lat_seconds = latLonDms.latitude.seconds < 10 ? ("0" + latLonDms.latitude.seconds).slice(-2) : latLonDms.latitude.seconds;
        let aux_lon_degres = this.addLonLeadingZeros(latLonDms.longitude.degrees.toString());
        let aux_lon_minutes = latLonDms.longitude.minutes < 10 ? ("0" + latLonDms.longitude.minutes).slice(-2) : latLonDms.longitude.minutes;
        let aux_lon_seconds = latLonDms.longitude.seconds < 10 ? ("0" + latLonDms.longitude.seconds).slice(-2) : latLonDms.longitude.seconds;
        this.latitude = latLonDms.latitude.cardinal + aux_lat_degres + "º " + aux_lat_minutes + "' " + aux_lat_seconds;
        this.longitude = latLonDms.longitude.cardinal + aux_lon_degres + "º " + aux_lon_minutes + "' " + aux_lon_seconds;
      }
    });

    this.getMap('map').on('pointerdrag', (evt) => {
      const extent = this.getMap('map').getView().calculateExtent(this.getMap('map').getSize());

    });
  }

  addLonLeadingZeros(num_string: string): string {
    let num: number = Number.parseFloat(num_string);
    if (num < 0) {
      if (num > -10) {
        const withoutMinus = num_string.slice(1);
        return '-' + '00' + withoutMinus;
      } else if (num > -100) {
        const withoutMinus = num_string.slice(1);
        return '-' + '0' + withoutMinus;
      } else {
        return num_string;
      }
    }

    if (num < 10) {
      return '00' + num_string;
    }

    if (num < 100) {
      return '0' + num_string;
    }

    return num_string;
  }

  addLatLeadingZeros(num_string: string): string {
    let num: number = Number.parseFloat(num_string);
    if (num < 0) {
      if (num > -10) {
        const withoutMinus = num_string.slice(1);
        return '-' + '0' + withoutMinus;
      } else {
        return num_string;
      }
    }

    if (num < 10) {
      return '0' + num_string;
    }

    return num_string;

  }

  activateStaticLayer(el) {
    this.activateLayer(el);
    if (!el.visible) {
      this.userSettingsService.deleteStaticLayerFromSettings(el);
    }
    else {
      this.userSettingsService.addStaticLayer(el);
    }
  }

  setLayers(layers:ParentLayers[]){
    this.layers = layers;
  }

  deactivateAllLayers(layer : ConfigurationLayerModel){
    this.configurationService.currentConfiguration.layers.forEach(e => {
      if(layer.id != e.id){
        e.visible = false;
      }
    });
  }
  addLayerToMap(layer : ConfigurationLayerModel){
    if (layer.visible) {
      layer.tileLayer.set("layerId", layer.id);
      this.map.getLayers().insertAt(1, layer.tileLayer);
      layer.tileLayer.setVisible(true);
    }
    else{
      let mapLayers:TileLayer<TileSource>[] = this.map.getLayers().getArray() as TileLayer<TileSource>[];
      let index = mapLayers.findIndex(mapLayer => {
        return mapLayer.get("layerId") == layer.id;
        }
      )

      //mapLayers.findIndex(layer => layer.getSource().get('url'))
      if(index != -1){
        mapLayers[index].setVisible(false);
        mapLayers.splice(index,1);
      }
    }
  }
  startUiBlock(event){
    this.uiBlock.startBlock('Loading Layer');
  }
  activateLayer(layer : ConfigurationLayerModel){
    if(layer.type == "Layer" || layer.type == "ImageLayer"){
      //this.deactivateAllLayers(layer);
      layer.visible = !layer.visible;
      if(layer.tileLayer == null){
        if(layer.type == "Layer") {
          if(layer.path == "OSM"){
            layer.tileLayer = new TileLayer({
              source: new OSM(),
              visible: false,
              zIndex: Number.MIN_SAFE_INTEGER
            });
          } else {
            layer.tileLayer = new TileLayer({
              source: new TileWMS({
                url: layer.path,
                params: {},
                serverType: 'geoserver'
              }),
              visible: false,
              zIndex: Number.MIN_SAFE_INTEGER
            });
            if (layer.visible) {
              let uiBlockCounter = 0;
              // Define the handlers
              const onTileLoadStart = () => {
                this.uiBlock.startBlock('Loading Layer');
                uiBlockCounter++;
              };
              const onTileLoadEnd = () => {
                for (uiBlockCounter; uiBlockCounter > 0; uiBlockCounter--){
                  this.uiBlock.stopBlock();
                }
                cleanupEvents();
              };
              const onTileLoadError = () => {
                for (uiBlockCounter; uiBlockCounter > 0; uiBlockCounter--) {
                  this.uiBlock.stopBlock();
                }
                cleanupEvents()
              };
              // Register the events
              layer.tileLayer.getSource().on('tileloadstart', onTileLoadStart);
              layer.tileLayer.getSource().on('tileloadend', onTileLoadEnd);
              layer.tileLayer.getSource().on('tileloaderror', onTileLoadError);
              // Function to clean up events
              const cleanupEvents = () => {
                const source = layer.tileLayer.getSource();
                source.un('tileloadstart', onTileLoadStart);
                source.un('tileloadend', onTileLoadEnd);
                source.un('tileloaderror', onTileLoadError);
              };
            }
            this.addLayerToMap(layer);
            return;
          }
        } else if(layer.type == "ImageLayer") {
          // Cria um ImageLayer ao invés de TileLayer
          const ImageLayer = require('ol/layer/Image').default;
          const ImageWMS = require('ol/source/ImageWMS').default;
          layer.tileLayer = new ImageLayer({
            source: new ImageWMS({
              url: layer.path,
              params: {},
              serverType: 'geoserver'
            }),
            visible: false,
            zIndex: Number.MIN_SAFE_INTEGER
          });
        }
      }
      this.addLayerToMap(layer);
    }
    else if(layer.type == 'GeoJsonLayer'){
      layer.visible = !layer.visible;
      if(layer.vectorLayer == null){
        this.populateGeoJsonLayer(layer);
      }
      else{
        let mapLayers:VectorLayerDMTR[] = this.map.getLayers().getArray() as VectorLayerDMTR[];
        let mapLayer = mapLayers.find(l => l.get('layerId') === layer.id);
        mapLayer?.setVisible(layer.visible)
        layer.vectorLayer.olLayer.setVisible(layer.visible);
      }
    }
  }

  removeLayerFromMap(layerId: number) {
    const existingLayer = this.map.getLayers().getArray().find(l => l.get('layerId') == layerId);
    if (existingLayer)
      this.map.removeLayer(existingLayer);
  }


  populateGeoJsonLayer(layer: ConfigurationLayerModel) {
    if (layer.type != 'GeoJsonLayer') {
      return;
    }

    this.removeLayerFromMap(layer.id);

    layer.vectorLayer = null;
    this.http.get(layer.path)
      .subscribe((geoJson: any) => {
        layer.vectorLayer =
        {
          id: JSON.stringify(layer.id),
          name: layer.name,
          image: null,
          visible: layer.visible,
          olLayer: new VectorLayerDMTR({
            source: new VectorSource({
              features: new GeoJSON().readFeatures(geoJson, {
                featureProjection: this.rcsControl.activeRCS.isactive ? this.rcsControl.activeRCS.epsg : "EPSG:3857",
                dataProjection: 'CRS:84',
              }),
              format: new GeoJSON({
                featureProjection: this.rcsControl.activeRCS.isactive ? this.rcsControl.activeRCS.epsg : "EPSG:3857",
                dataProjection: 'CRS:84',
              }),
            })
          })
        };

        if (this.userSettingsService.getGeoJsonStaticLayer(layer.id) == null) {
          let featureProperties = layer.vectorLayer.olLayer.getSource().getFeatures()[0].getProperties();
          let labels: string[] = [];
          for (const key in featureProperties) {
            if (key != 'geometry' && key != 'elevationLegendPos' && key != 'color') {
              labels.push(key);
            }
          }
          this.userSettingsService.addGeoJsonStaticLayer(layer, labels);
        }

        let userlayer: UserLayer = this.userSettingsService.getGeoJsonStaticLayer(layer.id);
        this.userAddedLayersService.changeLayerStyle(layer.vectorLayer.olLayer.getSource().getFeatures(), userlayer);
        layer.vectorLayer.olLayer.set("layerId", layer.id);
        this.map.getLayers().insertAt(1, layer.vectorLayer.olLayer);
        layer.vectorLayer.olLayer.setVisible(layer.visible);
      },
        (error) => console.error(error)
    )
  }



  /**
   * Create a map
   * @param id map id
   * @returns [ol.Map](https://openlayers.org/en/latest/apidoc/module-ol_Map-Map.html) the map
   */
  private createMap(id): void {

    let proj4 = (proj4x as any).default;
    proj4.defs('EPSG:3395', '+proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +ellps=WGS84 +datum=WGS84 +units=m +no_defs');
    register(proj4);
    olProj.get('EPSG:3395').setExtent([-20037508.342789244, -20037508.342789244, 20037508.342789244, 20037508.342789244]);

    var myFormat = function(dgts)
    {
      return (
        function(coordinate) {
            var coord2 = [coordinate[1], coordinate[0]];
            var DMS = olCoordinate.toStringHDMS(coord2,0);
            return `${coordinate[1].toFixed(dgts)}, ${coordinate[0].toFixed(dgts)}  ` + DMS;
      });
    }


    /*this.mousePointer = new MousePosition({
      coordinateFormat: myFormat(4),
      projection: 'EPSG:3395',
      // comment the following two lines to have the mouse position
      // be placed within the map.
    });*/

    var projection = olProj.get('EPSG:3395');
    var projectionExtent = projection.getExtent();
    var size = getWidth(projectionExtent) / 256;
    var resolutions = new Array(20);
    var matrixIds = new Array(20);


    for (var z = 0; z < 20; ++z) {
      // generate resolutions and matrixIds arrays for this WMTS
      resolutions[z] = size / Math.pow(2, z);
      matrixIds[z] = z;
    }

    // let control = new ScaleLine({
    //   units: 'metric',
    //   bar: true,
    //   steps: 2,
    //   text: true,
    //   minWidth: 140,
    // });

    //https://ext-test.eurocontrol.int/arcgis/rest/services/BASEMAPS/BASIC_MAP/MapServer/tile/0/0/0
    this.map = new olMap({
      //controls: defaultControls().extend([this.mousePointer]),
      target: id,
      interactions: defaultInteractions({
        doubleClickZoom: false,
      }),
      layers: [
      ],
      view: new View({
        //projection: 'EPSG:4326',
        center: olProj.fromLonLat([7.0785, 51.4614], this.rcsControl.activeRCS.isactive? this.rcsControl.activeRCS.epsg : "EPSG:3857"),
        //center: [7.0785, 51.4614],
        zoom: 5
      })
    });

    var thisMap = this.map;
    var thisVar = this;
    fetch('https://ext-test.eurocontrol.int/arcgis/rest/services/AGVN3/BASEMAPS/MapServer/WMTS/1.0.0/WMTSCapabilities.xml')
    .then(function (response) {
      return response.text();
    })
    .then(function (text) {

      var parser = new WMTSCapabilities();
      var result = parser.read(text);
      var options = optionsFromCapabilities(result, {
        layer: 'AGVN3_BASEMAPS',
      });

      options.urls = ['https://ext-test.eurocontrol.int/arcgis/rest/services/AGVN3/BASEMAPS/MapServer/WMTS/tile/1.0.0/AGVN3_BASEMAPS/{Style}/{TileMatrixSet}/{TileMatrix}/{TileRow}/{TileCol}.png'];
      options.crossOrigin = "anonymous";

      var layers = thisMap.getLayers();
      layers.insertAt(0,new TileLayer({
        opacity: 1,
        source: new WMTS(options),
        zIndex: Number.MIN_SAFE_INTEGER
      }))


    });


    this.selectionSource = new VectorSource({wrapX: false});
    this.selectionLayer = new VectorLayer({
      source: this.selectionSource,
    });

    this.map.addLayer(this.selectionLayer);
  }




  /**
  * Get a map. If it doesn't exist it will be created.
  * @param id id of the map or an objet with a getId method (from mapid service), default 'map'
  */
  getMap(id: string): olMap {
    // Create map if not exist
    if (!this.map) {
      this.createMap(id);
    }
    // return the map
    EventListenersOnMap.setMap(this.map);
    return this.map;
  }



  getMaps() {
    return this.getMap('map');
  }

  convertMetersToNauticalMiles(value:number):number{
    return Math.round((value * 0.000539956803) * 1000000) / 1000000;
  }

  public clearSelections():void{

    for(const parent of this.layers){
      for(const property in parent.layersSelector){
        parent.layersSelector[property] = false;
      }
    }

    this.selectionSource.clear();

    this.map.removeInteraction(this.draw);
    this.map.removeInteraction(this.select);


  }

    public clearSelectionsExcept():void{
      if (this.layers && Array.isArray(this.layers)) {
        for (const parent of this.layers) {
          const ls = parent.layersSelector;
          if (ls) {
            ls.pointer = false;
            ls.polygon = false;
            ls.circle  = false;
          }
        }
      }
      this.selectionSource.clear();

      this.map.removeInteraction(this.draw);
      this.map.removeInteraction(this.select);
    }


  public clearCoordinatePointsSelections() {
    if (this.select) {
      const featuresSelectedCollection = this.select.getFeatures();
      for (let i = featuresSelectedCollection.getLength() - 1; i >= 0; i--) {  // Iterate backward
        const feature = featuresSelectedCollection.item(i);
        if (feature.getGeometry() instanceof MultiPoint) {
          featuresSelectedCollection.remove(feature);  // Remove the feature safely
        }
      }
    }
  }

  public reprojectMap(projection:string): void{
    this.map.setView(new View({
      projection: projection,
      center: olProj.fromLonLat([7.0785, 51.4614], projection),
      //center: [7.0785, 51.4614],
      zoom: 5
    }));
  }

  public changeMousePointerProjection(projection : string) : void {
      this.mousePointer.setProjection(projection);
  }

  public setAreaView(latitude: number = 787975.0155801871, longitude: number = 6703319.46384995, zoom: number = 5): void{
    const projection = this.rcsControl.activeRCS.isactive ? this.rcsControl.activeRCS.epsg : "EPSG:3857";
    this.map.setView(new View({
      projection: projection,
      center: olProj.fromLonLat([longitude, latitude], projection),
      zoom: zoom
    }));
  }

  public shiftCenterByPixels(offsetXPx: number = 0, offsetYPx: number = 0): void {
    if (!this.map) {
      return;
    }

    const size = this.map.getSize();
    if (!size || size.length < 2) {
      return;
    }

    const view = this.map.getView();
    if (!view) {
      return;
    }

    const targetPixel: [number, number] = [
      size[0] / 2 + offsetXPx,
      size[1] / 2 + offsetYPx
    ];
    const targetCoordinate = this.map.getCoordinateFromPixel(targetPixel);
    if (!targetCoordinate) {
      return;
    }

    view.setCenter(targetCoordinate);
  }

  public zoomTo(latitude: number, longitude: number, zoom : number) : void{
    this.map.setView(new View({
      center: olProj.fromLonLat([longitude, latitude],this.rcsControl.activeRCS.isactive? this.rcsControl.activeRCS.epsg : "EPSG:3857"),
      //center: [7.0785, 51.4614],
      zoom: zoom
    }));
  }

  public zoomToFeatures(features: Feature<Geometry>[]){
    if (features && features.length > 0){
      const featureExtent = olExtent.createEmpty();
      features.forEach(feature=>{
        olExtent.extend(featureExtent, feature.getGeometry().getExtent());
      });
      this.getMap('map').getView().fit(featureExtent);
    }
  }

  public zoomToLayers(layers: any){
    if(layers && (layers.length > 0) && (layers[0] instanceof TopLevelLayers)){
      for (const layer of layers as TopLevelLayers[]){
        this.zoomToLayersImpl(layer.layers);
      }
    } else if (layers && (layers.length > 0)){
      this.zoomToLayersImpl(layers as Layer[]);
    } else {
      return;
    }
  }

  private zoomToLayersImpl(layers: Layer[]){
    let atLeastOneFeature = false;
    const featureExtent = olExtent.createEmpty();
    for (const layer of layers){
      const features: Feature<Geometry>[] = layer.olLayer.getSource().getFeatures();
      if (features && features.length > 0){
        features.forEach(feature=>{
          olExtent.extend(featureExtent, feature.getGeometry().getExtent());
        });
        atLeastOneFeature = true;
      }
    }
    if (atLeastOneFeature){
      this.getMap('map').getView().fit(featureExtent);
    } else {
      this.modalService.showMesssageModal(
        'Zoom To Layer',
        'No feature found in layer, zoom not executed.'
      );
    }
  }

  public moveLayersToFront(layers: any){
    if(layers && (layers.length > 0) && (layers[0] instanceof TopLevelLayers)){
      for (const layer of layers as TopLevelLayers[]){
        this.moveLayersToFrontImpl(layer.layers);
      }
    }

    else if (layers && (layers.length > 0)){
      this.moveLayersToFrontImpl(layers as Layer[]);
    } else {
      return;
    }
  }
  public moveLayersToBack(layers: any){
    if(layers && (layers.length > 0) && (layers[0] instanceof TopLevelLayers)){
      for (const layer of layers as TopLevelLayers[]){
        this.moveLayersToBackImpl(layer.layers);
      }
    } else if (layers && (layers.length > 0)) {
      this.moveLayersToBackImpl(layers as Layer[]);
    } else {
      return;
    }
  }

  private moveLayersToFrontImpl(layers: Layer[]){
    if (this.layerFrontZIndex >= Number.MAX_SAFE_INTEGER){ // prevents the user from incrementing this.layerFrontZIndex indifinitely
      this.resetLayersFrontZIndex();
    }
    for (const layer of layers as Layer[]){
      layer.olLayer.setZIndex(this.layerFrontZIndex);
    }
    this.layerFrontZIndex++;
  }
  public moveLayersToFrontStaticLayers(layers: ConfigurationLayerModel[]){
    // if (this.layerFrontZIndex >= Number.MAX_SAFE_INTEGER){ // prevents the user from incrementing this.layerFrontZIndex indifinitely
    //   this.resetLayersFrontZIndex();
    // }
    for (const layer of layers){
      layer.tileLayer.setZIndex(this.layerFrontZIndex);
    }
    this.layerFrontZIndex++;
  }
  public moveLayersToBackStaticLayers(layers: ConfigurationLayerModel[]){
    // if (this.layerFrontZIndex <= Number.MIN_SAFE_INTEGER){ // prevents the user from decrementing this.layerBackZIndex indifinitely
    //   this.resetLayersBackZIndex();
    // }
    for (const layer of layers){
      layer.tileLayer.setZIndex(this.layerBackZIndex);
    }
    this.layerBackZIndex--;

  }
  private moveLayersToBackImpl(layers: Layer[]){
    if (this.layerFrontZIndex <= Number.MIN_SAFE_INTEGER){ // prevents the user from decrementing this.layerBackZIndex indifinitely
      this.resetLayersBackZIndex();
    }
    for (const layer of layers){
      layer.olLayer.setZIndex(this.layerBackZIndex);
    }
    this.layerBackZIndex--;
  }

  private resetLayersFrontZIndex(){
    this.layerFrontZIndex = 100;
    for (const layer of this.map.getLayers().getArray()){
      (layer instanceof VectorLayerDMTR)? layer.setZIndex(TopLevelLayers.DEFAULT_Z_INDEX): false;
    }
  }

  private resetLayersBackZIndex(){
    this.layerBackZIndex = -100;
    for (const layer of this.map.getLayers().getArray()){
      (layer instanceof VectorLayerDMTR)? layer.setZIndex(TopLevelLayers.DEFAULT_Z_INDEX): false;
    }
  }

  public getCurrentExtent(): [number, number, number, number] {
    const map = this.getMap('map');
    const extent = map.getView().calculateExtent(map.getSize()) as [number, number, number, number];
    const projectionCode = map.getView().getProjection().getCode();
    // Garante sempre retorno em EPSG:4326
    if (projectionCode === 'EPSG:4326') {
      return extent;
    } else {
      return olProj.transformExtent(extent, projectionCode, 'EPSG:4326') as [number, number, number, number];
    }
  }

}
